import { motion } from "framer-motion";
import { ArrowRight, CalendarRange, Users, BedDouble, UtensilsCrossed } from "lucide-react";
import { EQUIPMENT } from "@/constants/aero";

const ease = [0.16, 1, 0.3, 1];

const HIGHLIGHTS = [
  { icon: CalendarRange, title: "3 Days", sub: "Hands-on Training" },
  { icon: Users, title: "Only 10 Seats", sub: "Small Batch" },
  { icon: BedDouble, title: "Stay Included", sub: "Hotel" },
  { icon: UtensilsCrossed, title: "Meals & Transport", sub: "Included" },
];

const FLOATERS = [
  { title: "Next Batch", sub: "October • 10 Seats", cls: "top-16 left-4 xl:left-16", delay: 0 },
  { title: "DJI Avata 2", sub: "Hands-on FPV Training", cls: "top-[42%] right-6 xl:right-16", delay: 0.8 },
  { title: "Creator Experience", sub: "Not an Online Course", cls: "bottom-60 left-10 xl:left-32", delay: 1.6 },
];

const AVATA_IMG = EQUIPMENT.find((e) => e.name.includes("Avata"))?.img;

const FILMSTRIP = [
  { img: "/assets/hero-main.jpg", caption: "Cinematic FPV Skills" },
  { img: "/assets/bts-9.jpg", caption: "Learn in Real Locations" },
  { img: AVATA_IMG, caption: "Hands-on DJI Avata 2" },
  { img: "/assets/bts-1.jpg", caption: "Small Batch. Big Experience." },
];

const FloatCard = ({ title, sub, cls, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: 0.3 + delay * 0.15, ease }}
    className={`absolute ${cls} z-20`}
  >
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay }}
      className="bg-white/95 backdrop-blur rounded-2xl shadow-[0_20px_50px_-15px_rgba(15,42,67,0.5)] px-5 py-3.5 border border-white/60"
    >
      <p className="font-display font-bold text-[15px] text-[#0F2A43]">{title}</p>
      <p className="mt-0.5 font-mono text-[11px] tracking-[0.18em] uppercase text-[#4F6D8A]">{sub}</p>
    </motion.div>
  </motion.div>
);

export const FilmSchoolTeaser = () => (
  <section
    data-testid="film-school-teaser"
    className="relative bg-gradient-to-br from-[#0F2A43] via-[#12305A] to-[#0B1F33] overflow-hidden"
  >
    {/* Mobile — image first, full width */}
    <div className="lg:hidden relative h-[340px]">
      <img
        src="/assets/fs-teaser.jpg"
        alt="Akhilesh Kumar Suman with DJI Avata 2 and FPV goggles"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-[center_30%]"
        style={{ WebkitMaskImage: "linear-gradient(to bottom, black 70%, transparent)", maskImage: "linear-gradient(to bottom, black 70%, transparent)" }}
      />
    </div>

    {/* Desktop — full-bleed image pinned to the right edge */}
    <div className="hidden lg:block absolute inset-y-0 right-0 w-[55%]">
      <motion.img
        src="/assets/fs-teaser.jpg"
        alt="Akhilesh Kumar Suman with DJI Avata 2 and FPV goggles"
        loading="lazy"
        initial={{ opacity: 0, x: 40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease }}
        data-testid="teaser-image"
        className="absolute inset-0 w-full h-full object-cover object-[center_30%]"
        style={{
          WebkitMaskImage: "linear-gradient(to right, transparent, black 26%)",
          maskImage: "linear-gradient(to right, transparent, black 26%)",
        }}
      />
      {FLOATERS.map((f) => (
        <FloatCard key={f.title} {...f} />
      ))}
    </div>

    <div className="relative max-w-[1320px] mx-auto px-6 md:px-10">
      <div className="lg:max-w-[46%] py-16 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease }}
        >
          <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-[#8FAECC] flex items-center gap-4">
            <span className="w-10 h-px bg-[#4F6D8A] inline-block" /> Film School
          </p>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl xl:text-[56px] leading-[1.05] text-white">
            DJI Avata 2 FPV Masterclass
          </h2>
          <p className="mt-4 font-mono text-[13px] font-bold tracking-[0.2em] uppercase text-[#8FAECC]">
            3 Days. Real Locations. Real Experience.
          </p>
          <p className="mt-5 text-white/70 leading-relaxed">
            Learn FPV flying from a real Bollywood aerial cinematographer through a hands-on 3-day immersive experience.
            This isn't an online course. <span className="text-white font-medium">You'll fly. You'll shoot. You'll create.</span>
          </p>

          {/* Highlights */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <motion.div
                  key={h.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.08, ease }}
                  data-testid={`teaser-highlight-${i}`}
                  className="flex items-center gap-3 bg-white/[0.07] border border-white/10 rounded-2xl px-4 py-3.5"
                >
                  <Icon size={18} strokeWidth={1.4} className="text-[#8FAECC] shrink-0" />
                  <div>
                    <p className="font-display font-bold text-[15px] text-white leading-tight">{h.title}</p>
                    <p className="mt-0.5 font-mono text-[11px] tracking-[0.16em] uppercase text-white/50">{h.sub}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTAs */}
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              data-testid="teaser-reserve"
              href="/film-school"
              className="inline-flex items-center gap-2 bg-white text-[#0F2A43] font-mono text-[13px] font-bold tracking-[0.2em] uppercase px-8 py-4 rounded-full shadow-[0_15px_35px_-10px_rgba(0,0,0,0.4)] hover:bg-[#8FAECC] hover:text-white hover:-translate-y-0.5 transition-all duration-300"
            >
              Reserve Your Seat <ArrowRight size={13} />
            </a>
            <a
              data-testid="teaser-explore"
              href="/film-school"
              className="inline-flex items-center gap-2 border border-white/35 text-white font-mono text-[13px] font-bold tracking-[0.2em] uppercase px-8 py-4 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300"
            >
              See the Full Experience
            </a>
          </div>
          <p className="mt-5 font-mono text-[12px] tracking-[0.22em] uppercase text-white/50">
            Starting at ₹12,000 per participant
          </p>
        </motion.div>
      </div>
    </div>

    {/* Bottom filmstrip — full width */}
    <div className="relative border-t border-white/10 bg-black/15">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {FILMSTRIP.map((f, i) => (
            <motion.figure
              key={f.caption}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08, ease }}
              data-testid={`teaser-strip-${i}`}
              className="group"
            >
              <div className="rounded-xl overflow-hidden border border-white/10">
                <img
                  src={f.img}
                  alt={f.caption}
                  loading="lazy"
                  className="w-full h-24 md:h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <figcaption className="mt-2.5 font-mono text-[11px] tracking-[0.18em] uppercase text-white/60 group-hover:text-white transition-colors">
                {f.caption}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </div>
  </section>
);
